import { action, makeObservable } from "mobx";
import { createRef } from "react";
import { injectable } from "tsyringe";
import { ViewModel } from "../../lib/vvm";

export interface AppleLoginWidgetViewProps {
  clientId: string;
  redirectUri?: string;
  onIdToken: (idToken: string) => void;
  onError?: (message: string) => void;
  disabled?: boolean;
}

const APPLE_SCRIPT_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

let appleScriptPromise: Promise<void> | null = null;

function ensureAppleScriptLoaded(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("window is not available"));
  }

  if (window.AppleID?.auth) {
    return Promise.resolve();
  }

  if (appleScriptPromise) {
    return appleScriptPromise;
  }

  appleScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${APPLE_SCRIPT_SRC}"]`,
    );
    if (existing) {
      if (existing.dataset.loaded === "true" || window.AppleID?.auth) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Не удалось загрузить AppleID script")),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.src = APPLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () =>
      reject(new Error("Не удалось загрузить AppleID script"));
    document.head.appendChild(script);
  })
    .catch((error) => {
      appleScriptPromise = null;
      throw error;
    })
    .then(() => undefined);

  return appleScriptPromise;
}

@injectable()
export class AppleLoginWidgetViewModel extends ViewModel<
  unknown,
  AppleLoginWidgetViewProps
> {
  readonly buttonRef = createRef<HTMLButtonElement>();
  private disposed = false;

  constructor() {
    super();
    makeObservable(this);
  }

  protected onViewMounted(): void {
    void this.initAppleAuth();
    this.reaction(
      () => [this.viewProps.clientId, this.viewProps.redirectUri],
      () => {
        void this.initAppleAuth();
      },
    );
    this.addDisposer(() => {
      this.disposed = true;
    });
  }

  private async initAppleAuth(): Promise<void> {
    const clientId = this.viewProps.clientId.trim();
    if (!clientId) {
      return;
    }

    try {
      await ensureAppleScriptLoaded();
      if (this.disposed) {
        return;
      }

      if (!window.AppleID?.auth) {
        this.viewProps.onError?.(
          "Apple SDK не инициализировался. Проверьте блокировку скриптов.",
        );
        return;
      }

      const redirectUri =
        this.viewProps.redirectUri?.trim() || window.location.href;
      window.AppleID.auth.init({
        clientId,
        scope: "name email",
        redirectURI: redirectUri,
        usePopup: true,
        state: crypto.randomUUID(),
        nonce: crypto.randomUUID(),
      });
    } catch (error) {
      this.viewProps.onError?.(
        error instanceof Error
          ? error.message
          : "Не удалось инициализировать Apple вход",
      );
    }
  }

  @action.bound
  async signIn(): Promise<void> {
    if (this.viewProps.disabled) {
      return;
    }

    const clientId = this.viewProps.clientId.trim();
    if (!clientId) {
      this.viewProps.onError?.("Не задан APPLE clientId");
      return;
    }

    await this.initAppleAuth();
    if (this.disposed || !window.AppleID?.auth) {
      return;
    }

    try {
      const result = await window.AppleID.auth.signIn();
      const token =
        typeof result?.authorization?.id_token === "string"
          ? result.authorization.id_token.trim()
          : "";
      if (!token) {
        this.viewProps.onError?.("Apple не вернул id_token");
        return;
      }

      this.viewProps.onIdToken(token);
    } catch (error) {
      this.viewProps.onError?.(
        error instanceof Error ? error.message : "Ошибка входа через Apple",
      );
    }
  }
}
